import React from 'react';
import { Check, Loader2, AlertCircle, Clock, Sparkles, Wand2, Film, CircleCheck } from 'lucide-react';
import type { ImagemotionImageGenerationStatus, ImagemotionServiceType, ImagemotionStatus } from '../types';

const STEPS = [
  { key: 'queued', label: 'Queued', description: 'Your project is in the processing queue', icon: Clock },
  { key: 'generating_clips', label: 'Generating Clips', description: 'AI is creating cinematic motion for each image', icon: Sparkles },
  { key: 'editing', label: 'Review & edit', description: 'Check your clips, trim or reorder, then render the final video', icon: Wand2 },
  { key: 'rendering', label: 'Rendering', description: 'Composing your final video with transitions and music', icon: Film },
  { key: 'completed', label: 'Completed', description: 'Your video is ready', icon: CircleCheck },
] as const;

interface StatusTrackerProps {
  status: ImagemotionStatus;
  errorMessage?: string | null;
  serviceType?: ImagemotionServiceType;
  generationStatus?: ImagemotionImageGenerationStatus | null;
}

export default function StatusTracker({
  status,
  errorMessage,
  serviceType = 'standard',
  generationStatus = null,
}: StatusTrackerProps) {
  const steps = STEPS.map((step) => {
    if (step.key === 'generating_clips' && serviceType === 'hyper_dusk') {
      let description = 'Generating dusk preview with Nano Banana';
      if (generationStatus === 'kling_processing') {
        description = 'Generating before/after timelapse from original to approved dusk image';
      } else if (generationStatus === 'dusk_ready' || generationStatus === 'awaiting_user_approval') {
        description = 'Waiting for your approval before animation';
      }
      return {
        ...step,
        label: generationStatus === 'kling_processing' ? 'Generating Animation' : 'Generating Dusk Preview',
        description,
      };
    }
    if (step.key === 'editing' && serviceType === 'hyper_dusk') {
      if (generationStatus === 'animation_ready' || generationStatus === 'completed') {
        return {
          ...step,
          label: 'Animation Ready',
          description: 'Approved animation clip is ready for final render/output',
        };
      }
      return {
        ...step,
        label: 'Awaiting Approval',
        description: 'Review the night version before triggering animation',
      };
    }
    return step;
  });
  const isFailed = status === 'failed';
  const isExpired = status === 'expired';
  const waitingOnUser = status === 'editing';
  const currentIndex = steps.findIndex((s) => s.key === status);
  const activeIndex = (isFailed || isExpired) ? -1 : currentIndex;

  return (
    <div>
      <div className="pl-1">
        {steps.map((step, i) => {
          const isComplete = activeIndex > i || (status === 'completed' && activeIndex === i);
          const isActive = activeIndex === i && status !== 'completed';
          const isLast = i === steps.length - 1;
          const StepIcon = step.icon;

          return (
            <div key={step.key} className="flex gap-4">
              {/* Left column: dot + connector */}
              <div className="flex flex-col items-center w-9 shrink-0">
                <div className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-500 ${
                  isComplete
                    ? 'bg-green-600/20 border-2 border-green-500/40'
                    : isActive
                    ? 'bg-blue-600/20 border-2 border-blue-500/50 shadow-lg shadow-blue-500/20'
                    : 'bg-slate-800/80 border-2 border-slate-700/50'
                }`}>
                  {isComplete ? (
                    <Check size={16} className="text-green-400" />
                  ) : isActive ? (
                    <StepIcon size={16} className="text-blue-400 animate-pulse" />
                  ) : (
                    <StepIcon size={14} className="text-slate-600" />
                  )}
                </div>
                {!isLast && (
                  <div className={`w-0.5 flex-1 my-1 transition-colors duration-500 ${
                    isComplete ? 'bg-green-500/40' : 'bg-slate-800'
                  }`} />
                )}
              </div>

              {/* Right column: content */}
              <div className={`flex-1 pb-4 pt-1 ${!isActive && !isComplete ? 'opacity-35' : ''}`}>
                <div className={`rounded-xl p-3 transition-all duration-500 ${
                  isActive
                    ? 'bg-blue-950/40 border border-blue-500/30'
                    : ''
                }`}>
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-semibold ${
                      isActive ? 'text-white' : isComplete ? 'text-slate-300' : 'text-slate-500'
                    }`}>
                      {step.label}
                    </p>
                    {isActive && !waitingOnUser && (
                      <Loader2 size={14} className="text-blue-400 animate-spin" />
                    )}
                    {isComplete && (
                      <span className="text-[10px] text-green-400 font-medium bg-green-500/10 px-1.5 py-0.5 rounded">Done</span>
                    )}
                  </div>
                  <p className={`text-xs mt-0.5 ${isActive ? 'text-slate-400' : 'text-slate-600'}`}>
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {isFailed && errorMessage && (
        <div className="mt-4 p-4 rounded-xl bg-red-900/15 border border-red-500/20">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-300">Processing failed</p>
              <p className="text-sm text-red-400/70 mt-1">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
